import { useEffect, useCallback, useMemo, useState } from "react"
import {
    COOKIE_CONSENT_NAME,
    DEFAULT_COOKIE_CATEGORIES,
    getCookieConsent,
    handleAcceptCategories,
    handleClearConsent,
    handleSendConsentStatus,
    setCookieConsent,
    CookieCategories,
    MessageTypes,
} from "QuorumGrassroots/framework/components/CookieConsent/helpers"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

const isCookieConsentEnabled = isFeatureEnabled("ff_ngg_action_center_cookie_consent")

export const CookieConsent = () => {
    const queryParams = useMemo(() => new URLSearchParams(window.location.search), [])

    const consentCategories = useMemo((): CookieCategories[] => {
        const categoriesFromURLasString = queryParams.get(COOKIE_CONSENT_NAME) ?? ""
        const categoriesFromURL = categoriesFromURLasString.split(",").filter(Boolean)
        const categoriesFromLocalStorage = getCookieConsent()
        const categories = !!categoriesFromURLasString ? categoriesFromURL : categoriesFromLocalStorage
        if (!categories.includes(CookieCategories.ESSENTIAL)) categories.push(CookieCategories.ESSENTIAL)
        return categories
    }, [queryParams])

    const [currentConsent, setCurrentConsent] = useState<CookieCategories[]>(consentCategories)

    const handleLoadCategories = (categories: CookieCategories[], requestId: string | null = null) => {
        setCurrentConsent(categories)
        setCookieConsent(categories)
        handleAcceptCategories(categories, requestId)
        window.location.reload()
    }

    const handleClearCategories = (requestId: string | null = null) => {
        setCookieConsent(DEFAULT_COOKIE_CATEGORIES)
        setCurrentConsent(DEFAULT_COOKIE_CATEGORIES)
        handleClearConsent(requestId)
    }

    const handleMessage = useCallback(
        (event) => {
            if (event.data.type === MessageTypes.SET_COOKIE_CONSENT) {
                const categories = event.data.categories || DEFAULT_COOKIE_CATEGORIES
                if (!categories.includes(CookieCategories.ESSENTIAL)) categories.push(CookieCategories.ESSENTIAL)
                handleLoadCategories(categories, event.data.requestId)
            } else if (event.data.type === MessageTypes.CLEAR_COOKIE_CONSENT) {
                handleClearCategories(event.data.requestId)
            } else if (event.data.type === MessageTypes.GET_COOKIE_CONSENT) {
                handleSendConsentStatus(event.data.requestId, currentConsent)
            }
        },
        [currentConsent],
    )

    useEffect(() => {
        if (consentCategories && isCookieConsentEnabled) {
            setCookieConsent(consentCategories)
            handleAcceptCategories(consentCategories)
        }

        window.addEventListener("message", handleMessage)
        return () => window.removeEventListener("message", handleMessage)
    }, [consentCategories, handleMessage])

    return null
}
